import React, { useEffect, useState, useCallback } from 'react';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  Skeleton,
  Paper,
  Alert,
  useTheme,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { useDealerGroup } from '../../../../context/DealerGroupContext';
import DualListMAIQ from 'app/components/DualListMAIQ';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { headerSaga } from 'app/pages/Header/saga';
import { headerActions, reducer, sliceKey } from 'app/pages/Header/slice';
import { selectDealersList } from 'app/pages/Header/selectors';

interface AddDealerGroupModalProps {
  open: boolean;
  onClose: () => void;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
}

export const AddDealerGroupModal: React.FC<AddDealerGroupModalProps> = ({
  open,
  onClose,
  setRefreshKey,
}) => {
  const { addDealerGroup, dealerGroup, setDealerGroup, error, setError } =
    useDealerGroup();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const dispatch = useDispatch();

  // Inject header reducer and saga
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  // Get dealers from header Redux state
  const dealersListDetails = useSelector(selectDealersList);
  const dealers = dealersListDetails.data?.body?.items || [];

  // Initialize dealerGroup state when modal opens
  useEffect(() => {
    if (open) {
      setDealerGroup({
        name: '',
        dealers: [],
        type: 'LIST',
        settings: {},
      });
      // Clear any existing error when modal opens
      setError('');
    }
  }, [open, setDealerGroup, setError]);

  // Memoized dealer list conversion
  const convertedDealers = useCallback(() => {
    return dealers.map(item => ({
      id: item._id.toString(),
      label: item.name,
    }));
  }, [dealers]);

  // Unified change handler
  const handleChange = e => {
    const { name, value } = e.target;
    setDealerGroup(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleListUpdate = updatedDealers => {
    setDealerGroup(prev => ({
      ...prev,
      dealers: updatedDealers.map(dealer => dealer.id),
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Only send clean user-editable fields
      const addPayload = {
        name: dealerGroup.name,
        dealers: dealerGroup.dealers,
        type: dealerGroup.type,
        settings: dealerGroup.settings
      };
      
      await addDealerGroup(addPayload, () => {
        setRefreshKey(prev => prev + 1);
        onClose();
        setDealerGroup({
          name: '',
          dealers: [],
          type: 'LIST',
          settings: {},
        });
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Initial data load only when modal opens
  useEffect(() => {
    if (open) {
      dispatch(headerActions.requestDealers());
    }
  }, [open]);

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{
          width: '1000px',
          maxWidth: '95vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                Add New Dealer Group
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Scrollable content area */}
        <Box
          component="form"
          id="dealer-group-form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            py: 2,
            px: 3,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Group Name *
            </Typography>
            <TextField
              fullWidth
              name="name"
              value={dealerGroup.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter group name"
              inputProps={{
                sx: { py: 1.5 },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Group Type *
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                name="type"
                value={dealerGroup.type || 'LIST'}
                onChange={handleChange}
                disabled={isSubmitting}
                sx={{ height: '42px' }}
              >
                <MenuItem value="LIST">LIST</MenuItem>
                <MenuItem value="GROUP">GROUP</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Business Dashboard URL for GROUP type */}
          {dealerGroup.type === 'GROUP' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Business Dashboard URL
              </Typography>
              <TextField
                fullWidth
                name="business_dashboard_url"
                value={dealerGroup.settings?.business_dashboard_url || ''}
                onChange={e => {
                  const value = e.target.value;
                  setDealerGroup(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      business_dashboard_url: value,
                    },
                  }));
                  setError('');
                }}
                disabled={isSubmitting}
                variant="outlined"
                size="small"
                placeholder="Enter business dashboard URL"
              />
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Assign Dealers
            </Typography>
            {dealers.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                <DualListMAIQ
                  key={`dual-list-${dealers.length}`}
                  availableItems={convertedDealers()}
                  selectedItems={dealerGroup.dealers || []}
                  onUpdate={handleListUpdate}
                />
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  width="100%"
                  height={350}
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Footer with actions - fixed */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              borderColor: 'divider',
              py: 0.75,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="dealer-group-form"
            onClick={e => {
              handleSubmit(e as any);
            }}
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: theme.palette.secondary.main,
              color: 'white',
              py: 0.75,
              px: 3,
              '&:hover': {
                bgcolor: '#0a1f3a',
              },
            }}
          >
            Create Group{' '}
            {isSubmitting && (
              <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />
            )}
          </Button>
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

export default AddDealerGroupModal;
